import { useEffect, useRef, useState } from 'react';

/**
 * `useGreeting()` — shared time-of-day + first-visit-aware greeting logic
 * used by the manager Dashboard and worker Find Shifts pages.
 *
 * Returns:
 *   • timeGreeting: "Good morning" | "Good afternoon" | "Good evening"
 *   • firstName: safe fallback ("there") when the user has no name yet
 *   • isFirstVisit: true only on the user's VERY first render of their
 *     role home page (per-user localStorage flag). Subsequent visits
 *     return false so a "Welcome back" copy can be shown.
 *
 * StrictMode + async-auth safety (iter97):
 *   The AuthContext often finishes loading AFTER the first render, so a
 *   lazy useState initializer sees `userId === undefined` and locks the
 *   result to false. We therefore compute the first-visit decision inside
 *   a useEffect keyed on `userId`. Idempotence under React 18 StrictMode's
 *   double-invoked effects is achieved via a useRef guard — the effect
 *   body reads localStorage, sets state, and (only if first-visit) writes
 *   the flag exactly once per user session.
 */
const FIRST_VISIT_KEY = (userId, role) => `sc365_first_dashboard_visit_${role}_${userId}`;

function timeOfDayGreeting(now = new Date()) {
  const h = now.getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

export default function useGreeting(user) {
  const userId = user?.id;
  const role = user?.role || 'user';
  const firstName = ((user?.fullName || user?.name || '').trim().split(/\s+/)[0]) || 'there';

  // Start `null` so the caller can render a neutral state until the
  // localStorage read has run. Consumers should treat `null` and `false`
  // both as "not first visit" for the subtitle branch, so this only
  // affects the very brief pre-effect window.
  const [isFirstVisit, setIsFirstVisit] = useState(false);
  const resolvedRef = useRef(false);

  useEffect(() => {
    if (!userId) return;
    if (resolvedRef.current) return; // idempotent under StrictMode
    resolvedRef.current = true;
    const key = FIRST_VISIT_KEY(userId, role);
    let seen = null;
    try { seen = localStorage.getItem(key); } catch { /* storage blocked */ }
    const first = seen !== '1';
    setIsFirstVisit(first);
    if (first) {
      try { localStorage.setItem(key, '1'); } catch { /* storage blocked */ }
    }
  }, [userId, role]);

  return {
    timeGreeting: timeOfDayGreeting(),
    firstName,
    isFirstVisit,
  };
}
